<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'
import OnboardingPersonalInfosForm from '@/components/collectivites/onboarding/OnboardingPersonalInfosForm.vue'
import OnboardingIdentityDocumentsForm from '@/components/collectivites/onboarding/OnboardingIdentityDocumentsForm.vue'
import OnboardingPayslipDocumentsForm from '@/components/collectivites/onboarding/OnboardingPayslipDocumentsForm.vue'

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const submitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const detail = ref<any>(null)
const collectivites = ref<Array<{ id: number; nom: string }>>([])
const personalFormRef = ref<any>(null)

const typesPieceIdentites: Record<string, string> = {
  '1': 'CNI',
  '2': 'Passeport',
}

async function fetchDetail() {
  loading.value = true
  errorMessage.value = ''
  try {
    const { data } = await axios.get(`/kyc/members/${route.params.id}`)
    detail.value = data
    if (data?.user?.collectivite) {
      collectivites.value = [{ id: data.user.collectivite.id, nom: data.user.collectivite.nom }]
    } else if (data?.user?.collectivite_id) {
      collectivites.value = [{ id: data.user.collectivite_id, nom: 'Collectivité' }]
    }
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors du chargement'
  } finally {
    loading.value = false
  }
}

const personalInitialData = computed(() => {
  const u = detail.value?.user
  if (!u) return null
  const k = detail.value?.kyc
  return {
    nom: k?.nom ?? u.nom ?? '',
    prenom: k?.prenom ?? u.prenom ?? '',
    date_naissance: (k?.date_naissance ?? u.date_naissance)
      ? String(k?.date_naissance ?? u.date_naissance).substring(0, 10)
      : '',
    age: u.age ?? 0,
    date_engagement: u.date_engagement ? String(u.date_engagement).substring(0, 10) : '',
    anciennete: u.anciennete ?? 0,
    collectivite_id: u.collectivite_id ?? null,
    salaire: u.salaire != null ? String(u.salaire) : '',
    quotite_cessible_pourcent: u.quotite_cessible_pourcent != null ? String(u.quotite_cessible_pourcent) : '',
    quotite_cessible_montant: u.quotite_cessible_montant != null ? String(u.quotite_cessible_montant) : '',
    telephone: u.telephone ?? '',
    email: u.email ?? '',
  }
})

async function onPersonalFormSaved(payload: any) {
  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    await axios.put(`/kyc/members/${route.params.id}`, {
      nom: payload.nom,
      prenom: payload.prenom,
      date_naissance: payload.date_naissance,
    })
    successMessage.value = 'Profil KYC enregistré'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de la sauvegarde'
  } finally {
    submitting.value = false
  }
}

function triggerSavePersonal() {
  personalFormRef.value?.onSubmit()
}

async function uploadKycDocument(typeDocument: string, file: any, extra: { numero?: string; expiration?: string } = {}) {
  const fd = new FormData()
  fd.append('type_document', typeDocument)
  fd.append('fichier', file)
  if (extra.numero) fd.append('numero', extra.numero)
  if (extra.expiration) fd.append('expiration', extra.expiration)
  await axios.post(`/kyc/members/${route.params.id}/documents`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

async function onIdentityDocumentsSaved(payload: any) {
  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const typeLabel = typesPieceIdentites[payload.type_piece_identite] || 'cni'
    const isPassport = String(typeLabel).toLowerCase().includes('pass')

    if (payload.photo?.file) {
      await uploadKycDocument('photo', payload.photo.file)
    }
    if (payload.piece_recto?.file) {
      await uploadKycDocument(isPassport ? 'passeport' : 'cni_recto', payload.piece_recto.file, {
        numero: payload.numero_piece_identite,
      })
    }
    if (payload.piece_verso?.file && !isPassport) {
      await uploadKycDocument('cni_verso', payload.piece_verso.file, {
        numero: payload.numero_piece_identite,
      })
    }
    successMessage.value = "Pièces d'identité enregistrées"
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || "Erreur lors de l'enregistrement des pièces"
  } finally {
    submitting.value = false
  }
}

const lastUploadedBulletins = ref<Record<string, string>>({})

async function onPayslipDocumentsSaved(payload: any) {
  // Le composant frontoffice émet 'saved' à chaque upload — on upload uniquement le bulletin nouvellement ajouté
  for (const key of ['bulletin1', 'bulletin2', 'bulletin3'] as const) {
    const item = payload[key]
    if (item?.file && lastUploadedBulletins.value[key] !== item.name) {
      submitting.value = true
      errorMessage.value = ''
      try {
        await uploadKycDocument(key, item.file)
        lastUploadedBulletins.value[key] = item.name
        successMessage.value = `Bulletin ${key.replace('bulletin', '')} enregistré`
      } catch (e: any) {
        errorMessage.value = e.response?.data?.message || "Erreur lors de l'upload du bulletin"
      } finally {
        submitting.value = false
      }
    }
  }
  await fetchDetail()
}

async function validateKyc() {
  if (!confirm('Confirmer la validation du KYC de ce membre ?')) return
  submitting.value = true
  errorMessage.value = ''
  try {
    await axios.post(`/kyc/members/${route.params.id}/validate`)
    successMessage.value = 'KYC validé'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de la validation'
  } finally {
    submitting.value = false
  }
}

async function rejectKyc() {
  if (!confirm('Confirmer le rejet du KYC de ce membre ?')) return
  submitting.value = true
  errorMessage.value = ''
  try {
    await axios.post(`/kyc/members/${route.params.id}/reject`)
    successMessage.value = 'KYC rejeté'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors du rejet'
  } finally {
    submitting.value = false
  }
}

async function deleteDocument(docId: number) {
  if (!confirm('Supprimer ce document ?')) return
  submitting.value = true
  try {
    await axios.delete(`/kyc/members/${route.params.id}/documents/${docId}`)
    successMessage.value = 'Document supprimé'
    await fetchDetail()
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors de la suppression'
  } finally {
    submitting.value = false
  }
}

function statutLabel(): { label: string; cls: string } {
  const s = detail.value?.kyc?.statut
  if (!detail.value?.kyc) return { label: 'Pas de KYC', cls: 'tag-state warning stroke' }
  if (s === 'verified') return { label: 'Validé', cls: 'tag-state success stroke' }
  if (s === 'rejected') return { label: 'Rejeté', cls: 'tag-state danger stroke' }
  return { label: 'En attente', cls: 'tag-state warning stroke' }
}

onMounted(fetchDetail)
</script>

<template>
  <section class="container-full-5">
    <button class="btn white2 mb-4" @click="router.back()">← Retour</button>

    <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>
    <div v-if="successMessage" class="alert alert-success mb-4">{{ successMessage }}</div>

    <div v-if="loading" class="flex-center py-10">
      <div class="loader dark"></div>
    </div>

    <div v-else-if="detail">
      <!-- En-tête membre + statut + actions -->
      <div class="s20 widget p-6 mb-5">
        <div style="display: flex; align-items: center; gap: 1.5rem;">
          <div style="width: 96px; height: 96px; min-width: 96px; flex: 0 0 96px; border-radius: 15px; overflow: hidden;">
            <img :src="detail.user.photo_url || '/public/assets/users/300w/user2.jpg'" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
          </div>
          <div style="flex: 1 1 auto; min-width: 0;">
            <h3>{{ detail.user.nom }} {{ detail.user.prenom }}</h3>
            <p>{{ detail.user.email }}</p>
            <p>{{ detail.user.telephone }}</p>
            <p class="mt-2">
              Statut KYC :
              <span :class="statutLabel().cls">{{ statutLabel().label }}</span>
            </p>
          </div>
          <div class="flex gap-3">
            <button class="btn-ultra" :disabled="submitting || !detail.kyc" @click="validateKyc">
              <div class="btn-show">
                <img src="/public/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
                <span>Valider</span>
              </div>
            </button>
            <button class="btn-ultra danger" :disabled="submitting || !detail.kyc" @click="rejectKyc">
              <div class="btn-show">
                <img src="/public/icons/icon-denied.svg" class="btn-icon icon-white" alt="" />
                <span style="color: var(--color-white);">Rejeter</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Profil KYC : OnboardingPersonalInfosForm -->
      <div class="s20 widget p-6 mb-5">
        <h4 class="mb-4">Profil KYC</h4>
        <OnboardingPersonalInfosForm
          ref="personalFormRef"
          :collectivites="collectivites"
          :hide-collectivite-field="true"
          :hide-submit-button="true"
          :initial-data="personalInitialData"
          @saved="onPersonalFormSaved"
        />
        <div class="flex-right mt-3">
          <button class="btn-ultra" :disabled="submitting" @click="triggerSavePersonal">
            <div class="btn-show">
              <img src="/public/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
              <span>Enregistrer</span>
            </div>
          </button>
        </div>
      </div>

      <!-- Pièce d'identité + Bulletins : côte à côte sur desktop -->
      <div style="clear: both;"></div>
      <div class="mb-5" style="display: flex; gap: 1.5rem; flex-wrap: wrap; align-items: flex-start; clear: both;">
        <div class="widget p-6" style="flex: 1 1 calc(50% - 0.75rem); min-width: 320px;">
          <h4 class="mb-4">Pièce d'identité</h4>
          <OnboardingIdentityDocumentsForm
            :user="{ nom: detail.user.nom, prenom: detail.user.prenom, email: detail.user.email, telephone: detail.user.telephone }"
            :types_piece_identites="typesPieceIdentites"
            :hide-header="true"
            @saved="onIdentityDocumentsSaved"
          />
        </div>

        <div class="widget p-6" style="flex: 1 1 calc(50% - 0.75rem); min-width: 320px;">
          <h4 class="mb-4">Bulletins de salaire</h4>
          <OnboardingPayslipDocumentsForm
            :form-params="{ user: detail.user, photo: null }"
            :loading="submitting"
            :hide-header="true"
            :hide-title="true"
            :hide-previous-link="true"
            @saved="onPayslipDocumentsSaved"
          />
        </div>
      </div>

      <!-- Documents existants -->
      <div class="s20 widget p-6 mb-5">
        <h4 class="mb-4">Documents existants</h4>
        <div v-if="!detail.kyc?.documents?.length" class="text-grey-500 py-3">
          Aucun document fourni.
        </div>
        <div v-else>
          <div v-for="d in detail.kyc.documents" :key="d.id" class="flex align-items-center gap-4 py-3 border-bottom">
            <div style="flex: 1 1 auto;">
              <strong>{{ d.typeDocument }}</strong>
              <span v-if="d.numero" class="ml-2 text-sm text-grey-500">N° {{ d.numero }}</span>
              <span v-if="d.expiration && d.expiration !== '--'" class="ml-2 text-sm text-grey-500">
                Exp. {{ d.expiration.substring(0, 10) }}
              </span>
            </div>
            <a v-if="d.fichierUrl" :href="d.fichierUrl" target="_blank" class="btn white2">Voir</a>
            <button class="btn-ultra danger" :disabled="submitting" @click="deleteDocument(d.id)">
              <div class="btn-show">
                <span style="color: var(--color-white);">Supprimer</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
